import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  LockClosedIcon,
  CheckBadgeIcon,
  DocumentTextIcon,
  HomeIcon,
  HeartIcon,
  ScaleIcon,
} from "@heroicons/react/24/outline";

// Matches translations.documents' order (Will, Property, Health, Power of Attorney).
const DOCUMENT_ICONS = [DocumentTextIcon, HomeIcon, HeartIcon, ScaleIcon];

const translations = {
  en: {
    documents: [
      { emoji: "📜", title: "Will & Testament", type: "Legal · Blockchain Anchored" },
      { emoji: "🏠", title: "Property Title", type: "Real Estate · Encrypted" },
      { emoji: "💉", title: "Health Records", type: "Medical · Encrypted" },
      { emoji: "⚖️", title: "Power of Attorney", type: "Legal · Encrypted" },
    ],
    features: [
      {
        icon: "🧠",
        title: "Run by AI Brain",
        desc: "An autonomous AI agent watches every upload, node, and continuity check around the clock, detecting and healing issues before they ever reach you — and it's available right in your dashboard any time you have a question, day or night.",
      },
      {
        icon: "⛓️",
        title: "Blockchain Anchored",
        desc: "Every document is hashed and recorded on-chain. Tamper-proof, timestamped, and permanently verifiable.",
      },
      {
        icon: "🔐",
        title: "End-to-End Encrypted",
        desc: "Industry-standard AES-256 encryption. Files are secured before leaving your device.",
      },
      {
        icon: "🌐",
        title: "Blockchain Storage",
        desc: "Stored on the blockchain network, content-addressed so any change to a file produces a completely different address.",
      },
      {
        icon: "👥",
        title: "Legal Contact Access",
        desc: "Designate trusted executors and family members. Access is released automatically via your continuity switch.",
      },
      {
        icon: "🛡️",
        title: "Immutable by Design",
        desc: "Once your document's hash is anchored to the blockchain, that proof of its existence and timing can't be altered or disputed by anyone — including us.",
      },
    ],
    chainSteps: [
      { step: "01", title: "Upload Your Document", desc: "Drag and drop your will, title deed, or legal file into your vault." },
      { step: "02", title: "Encrypted & Hashed", desc: "Your file is AES-256 encrypted. A unique cryptographic hash is generated." },
      { step: "03", title: "Anchored On-Chain", desc: "The hash is written to the blockchain — creating a permanent, tamper-proof record." },
      { step: "04", title: "Stored on IPFS", desc: "The encrypted file is distributed across IPFS nodes. No central server to attack." },
      { step: "05", title: "Legal Contact Access Released", desc: "When your continuity switch triggers, designated legal contacts gain access automatically." },
    ],
    hero: {
      titleLine1: "Your Legacy, Secured,",
      titleLine2: "On the Chain Forever",
      ctaPrimary: "Start your vault",
      ctaSecondary: "Login",
      pricingNote: "All plans start at $50 CAD",
      paragraph:
        "ShadowVault protects your legal and estate documents with blockchain immutability, AES-256 encryption, and decentralized storage. Your wishes — preserved exactly as you intended.",
      highValueNote: "Purpose-built for your most valuable documents — your Will, property deeds, and other high-value records.",
      stat1Label: "AES Encryption",
      stat2Label: "Data Sold",
      stat3Label: "Decentralized",
      stat4Label: "Access Anywhere",
      mockupTitle: "My Chain Vault",
      mockupSubtitle: "4 documents · blockchain verified",
      mockupFooter: "AES-256 • Blockchain Distributed",
    },
    how: {
      heading: "How ShadowVault Works",
      sub: "From upload to on-chain proof — in seconds.",
    },
    featuresHeading: "Why Choose ShadowVault?",
    trust: {
      heading: "When AI Can Fake It, We Prove It.",
      points: [
        {
          icon: "🔒",
          title: "Permanent by Design",
          desc: "Your records stay protected. No one—not even us—can delete them.",
        },
        {
          icon: "🔍",
          title: "Every Change Leaves a Trace",
          desc: "Every document gets a unique digital fingerprint. Change even one character, and verification reveals it.",
        },
        {
          icon: "🔑",
          title: "Access on Your Terms",
          desc: "Choose who can access your files, when, and under what conditions. Your rules. Your control.",
        },
      ],
    },
    cta: {
      heading: "Protect Your Legacy Today",
      button: "Start your vault",
    },
  },
  zh: {
    documents: [
      { emoji: "📜", title: "遗嘱文件", type: "法律 · 区块链锚定" },
      { emoji: "🏠", title: "房产证", type: "不动产 · 已加密" },
      { emoji: "💉", title: "健康记录", type: "医疗 · 已加密" },
      { emoji: "⚖️", title: "授权委托书", type: "法律 · 已加密" },
    ],
    features: [
      {
        icon: "🧠",
        title: "由AI大脑驱动",
        desc: "自主AI代理全天候监控每一次上传、每个节点与生命状态检查,在问题影响到您之前就主动发现并修复——同一个AI也随时在您的控制台中,为您解答任何疑问,全天候在线。",
      },
      {
        icon: "⛓️",
        title: "区块链锚定",
        desc: "每份文件都会生成哈希并记录上链,防篡改、加盖时间戳且永久可验证。",
      },
      {
        icon: "🔐",
        title: "端到端加密",
        desc: "行业标准AES-256加密,文件在离开您的设备前就已完成加密。",
      },
      {
        icon: "🌐",
        title: "区块链存储",
        desc: "存储于区块链网络——基于内容寻址,文件的任何改动都会产生完全不同的地址。",
      },
      {
        icon: "👥",
        title: "法定联系人权限",
        desc: "指定值得信赖的执行人与家庭成员。当您的生命状态开关触发时,访问权限会自动释放。",
      },
      {
        icon: "🛡️",
        title: "从设计上不可篡改",
        desc: "一旦文件哈希锚定至区块链,这份关于其存在与时间的证明就无法被任何人——包括我们自己——更改或质疑。",
      },
    ],
    chainSteps: [
      { step: "01", title: "上传您的文件", desc: "将遗嘱、产权证或其他法律文件拖放至您的保险库。" },
      { step: "02", title: "加密与哈希处理", desc: "文件经过AES-256加密,并生成唯一的加密哈希值。" },
      { step: "03", title: "锚定上链", desc: "哈希值被写入区块链——创建永久、防篡改的记录。" },
      { step: "04", title: "存储至IPFS", desc: "加密文件分布存储在IPFS节点中,没有可被攻击的中心服务器。" },
      { step: "05", title: "法定联系人权限释放", desc: "当您的生命状态开关触发时,指定的法定联系人将自动获得访问权限。" },
    ],
    hero: {
      titleLine1: "守护您的传承,",
      titleLine2: "永远上链留存",
      ctaPrimary: "开启您的保险库",
      ctaSecondary: "登录",
      pricingNote: "所有套餐均为 $50 加元起",
      paragraph:
        "ShadowVault 通过区块链不可篡改性、AES-256加密和分布式存储,保护您的法律与遗产文件。您的心愿——原样永存。",
      highValueNote: "专为您最重要的文件而设计——遗嘱、产权证等高价值文件。",
      stat1Label: "AES加密",
      stat2Label: "数据出售",
      stat3Label: "去中心化",
      stat4Label: "随时访问",
      mockupTitle: "我的链上保险库",
      mockupSubtitle: "4份文件 · 区块链已验证",
      mockupFooter: "AES-256 • 区块链分布式存储",
    },
    how: {
      heading: "ShadowVault 如何运作",
      sub: "从上传到链上存证——仅需几秒。",
    },
    featuresHeading: "为什么选择 ShadowVault?",
    trust: {
      heading: "当AI能伪造,我们能证明。",
      points: [
        {
          icon: "🔒",
          title: "设计即永久保存",
          desc: "您的文件将持续受到保护。包括我们自己,任何人都无法删除它们。",
        },
        {
          icon: "🔍",
          title: "任何改动都会留下痕迹",
          desc: "每份文件都拥有独一无二的数字指纹。哪怕只改动一个字符,验证时也会被发现。",
        },
        {
          icon: "🔑",
          title: "访问权限,由您决定",
          desc: "您可以自行选择谁能访问您的文件、何时访问,以及在什么条件下访问。您的规则,您的掌控。",
        },
      ],
    },
    cta: {
      heading: "立即守护您的传承",
      button: "开启您的保险库",
    },
  },
};

// The hero mockup's document list — reveals in sequence once on load (the
// page's one deliberate motion moment), rather than animating on every
// scroll or hover. Respects reduced-motion.
function DocumentRows({ documents }) {
  const reduceMotion = useReducedMotion();

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduceMotion ? 0 : 0.08, delayChildren: reduceMotion ? 0 : 0.15 } },
  };
  const row = reduceMotion
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } } };

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-2">
      {documents.map((doc, idx) => {
        const Icon = DOCUMENT_ICONS[idx] || DocumentTextIcon;
        return (
          <motion.div
            key={idx}
            variants={row}
            className="bg-dark-bg bg-opacity-40 border border-dark-border rounded-lg p-3 hover:border-primary transition flex items-center gap-3"
          >
            <div className="w-9 h-9 rounded-md bg-primary bg-opacity-10 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-primary" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{doc.title}</div>
              <div className="text-xs text-gray-400 truncate">{doc.type}</div>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-primary flex-shrink-0">
              <CheckBadgeIcon className="w-3.5 h-3.5" strokeWidth={2} />
              Verified
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

export default function Main() {
  const navigate = useNavigate();
  const { lang } = useOutletContext();

  const t = translations[lang];

  return (
    <div className="min-h-screen bg-dark-bg text-white">

      {/* ========== HERO ========== */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Left */}
          <div>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
              {t.hero.titleLine1}<br />
              <span className="text-primary">{t.hero.titleLine2}</span>
            </h1>
            <p className="text-xl text-gray-300 mb-4 leading-relaxed">
              {t.hero.paragraph}
            </p>
            <p className="text-sm text-gray-400 mb-8 flex items-center gap-1.5">
              <span>📜</span> {t.hero.highValueNote}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button
                onClick={() => navigate("/register")}
                className="px-9 py-4 bg-primary text-dark-bg rounded-xl font-semibold text-lg shadow-lg shadow-primary/20 transition-all duration-200 hover:bg-primary-dark hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 active:translate-y-0 active:shadow-md"
              >
                {t.hero.ctaPrimary}
              </button>
              <button
                onClick={() => navigate("/login")}
                className="px-9 py-4 border-2 border-primary/50 text-primary rounded-xl font-semibold text-lg transition-all duration-200 hover:border-primary hover:bg-primary/10 hover:-translate-y-0.5 active:translate-y-0"
              >
                {t.hero.ctaSecondary}
              </button>
            </div>

            <p className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1.5 mb-8 -mt-6">
              {t.hero.pricingNote}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="text-4xl font-bold text-primary mb-2">256-bit</div>
                <div className="text-xs text-gray-400 uppercase">{t.hero.stat1Label}</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">0</div>
                <div className="text-xs text-gray-400 uppercase">{t.hero.stat2Label}</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">Chain</div>
                <div className="text-xs text-gray-400 uppercase">{t.hero.stat3Label}</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">24/7</div>
                <div className="text-xs text-gray-400 uppercase">{t.hero.stat4Label}</div>
              </div>
            </div>
          </div>

          {/* Right - Mockup */}
          <div className="relative">
            <div className="bg-gradient-to-br from-dark-card to-dark-bg border border-dark-border rounded-2xl p-6 shadow-2xl shadow-black/20">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-dark-border">
                <div className="w-9 h-9 rounded-lg bg-primary bg-opacity-15 border border-primary border-opacity-30 flex items-center justify-center flex-shrink-0">
                  <LockClosedIcon className="w-4 h-4 text-primary" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-semibold text-sm">{t.hero.mockupTitle}</div>
                  <div className="text-xs text-gray-400">{t.hero.mockupSubtitle}</div>
                </div>
              </div>

              <DocumentRows documents={t.documents} />

              <div className="mt-5 pt-4 border-t border-dark-border flex items-center justify-between text-xs text-gray-400">
                <span>{t.hero.mockupFooter}</span>
                <span className="inline-flex items-center gap-1.5 text-primary font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Active
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== AI TRUST / PROOF ========== */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold">{t.trust.heading}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.trust.points.map((point, idx) => (
              <div
                key={idx}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-primary transition"
              >
                <div className="text-4xl mb-4">{point.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{point.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{point.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== HOW THE CHAIN WORKS ========== */}
      <section className="py-20 border-y border-dark-border bg-dark-card bg-opacity-30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-3">{t.how.heading}</h2>
            <p className="text-gray-400 text-lg">{t.how.sub}</p>
          </div>
          <div className="relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-30" />
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {t.chainSteps.map((s) => (
                <div key={s.step} className="flex flex-col items-center text-center relative">
                  <div className="w-16 h-16 rounded-full bg-primary bg-opacity-15 border-2 border-primary flex items-center justify-center text-primary font-bold text-lg mb-4 z-10">
                    {s.step}
                  </div>
                  <h4 className="font-semibold text-sm mb-2">{s.title}</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12">{t.featuresHeading}</h2>
          <div id="security" className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.features.map((feature, idx) => (
              <div
                key={idx}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-primary transition"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-dark rounded-2xl mx-6 mb-10 text-dark-bg">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold mb-8">{t.cta.heading}</h2>
          <button
            onClick={() => navigate("/register")}
            className="px-8 py-4 bg-dark-bg text-primary hover:bg-gray-900 rounded-lg font-semibold transition text-lg"
          >
            {t.cta.button}
          </button>
        </div>
      </section>

    </div>
  );
}
